import * as PIXI from "pixi.js";

import { TGameAssetAlias } from "@src/game/screens/GameScreen";
import { EventSystem } from "@src/core/events/EventSystem";
import { IView } from "@src/core/game/components/IView";
import { Assets } from "@src/core/assets/Assets";
import { constants } from "@src/game/Constants";
import gsap from "gsap";
import { Audio } from "@src/core/audio/Audio";

// TODO: Merge this and OutroIntroView into one class?
export class FreespinIntroView implements IView {
  private _view: PIXI.Container = new PIXI.Container();
  public get view(): PIXI.Container {
    return this._view;
  }

  private _introSprite!: PIXI.Sprite;

  private static readonly BASE_TEXT_STYLE = {
    fontFamily: "gameFont",
    fill: 0xffbb1e,
    fontWeight: "bold",
    wordWrap: false,
    letterSpacing: 3,
    align: "center",
    stroke: {
      color: 0x000033,
      width: 8,
      join: "round",
    },
  } as const;

  private _introWelcomeText!: PIXI.Text;
  private _introTapText!: PIXI.Text;
  private _introWelcomeTextStyleLandscape: PIXI.TextStyle = new PIXI.TextStyle({
    ...FreespinIntroView.BASE_TEXT_STYLE,
    fontSize: 35,
  });
  private _introWelcomeTextStylePortrait: PIXI.TextStyle = new PIXI.TextStyle({
    ...FreespinIntroView.BASE_TEXT_STYLE,
    fontSize: 45,
  });

  private _introTapTextStyleLandscape: PIXI.TextStyle = new PIXI.TextStyle({
    ...FreespinIntroView.BASE_TEXT_STYLE,
    fontSize: 25,
    fill: 0x00bfff,
  });
  private _introTapTextStylePortrait: PIXI.TextStyle = new PIXI.TextStyle({
    ...FreespinIntroView.BASE_TEXT_STYLE,
    fontSize: 35,
    fill: 0x00bfff,
  });

  private _backgroundGraphics!: PIXI.Graphics;

  private _hasRunShowAnimation: boolean = false;
  private _animationTween!: GSAPTween;

  public constructor() {
    this._setupEventListeners();

    this._initializeAssets();
    this._parentAssets();

    this._initializeComponents();
    this._parentComponents();
  }

  public playShowAnimation(): void {
    Audio.play("sndDialogScreenMove");
    this._animationTween?.kill();
    this._view.y = -1200;
    this._view.visible = true;
    this._hasRunShowAnimation = true;

    this._animationTween = gsap.to(this._view, {
      y: 0,
      duration: 0.5,
      ease: "back.out(1)",
      onStart: () => {
        EventSystem.invoke(
          constants.GameEvents.FREESPIN_INTRO_OPEN_ANIMATION_STARTED,
        );
      },
      onComplete: () => {
        EventSystem.invoke(
          constants.GameEvents.FREESPIN_INTRO_OPEN_ANIMATION_ENDED,
        );
      },
    });
  }

  public playHideAnimation(): void {
    if (!this._hasRunShowAnimation) {
      return;
    }
    Audio.play("sndDialogScreenMove");
    this._animationTween?.progress(1);
    this._animationTween?.kill();

    this._animationTween = gsap.to(this._view, {
      y: -1200,
      duration: 0.5,
      ease: "back.in(1)",
      onComplete: () => {
        this._view.visible = false;
        console.log("outro popup hidden");
        EventSystem.invoke(constants.GameEvents.FREESPIN_INTRO_CLOSED);
        this._hasRunShowAnimation = false;
      },
    });
  }

  private _setupEventListeners(): void {
    EventSystem.addListener(
      constants.GameEvents.LANDSCAPE_LAYOUT_REQUESTED,
      this._onLandscapeLayoutRequested.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.PORTRAIT_LAYOUT_REQUESTED,
      this._onPortraitLayoutRequested.bind(this),
    );
  }

  private _onLandscapeLayoutRequested(): void {
    this._introSprite.scale.set(1);

    this._introWelcomeText.position.set(0, -150);
    this._introTapText.position.set(0, 175);
    this._introWelcomeText.style = this._introWelcomeTextStyleLandscape;
    this._introTapText.style = this._introTapTextStyleLandscape;
  }

  private _onPortraitLayoutRequested(): void {
    this._introSprite.scale.set(1.25);

    this._introWelcomeText.position.set(0, -170);
    this._introTapText.position.set(0, 200);
    this._introWelcomeText.style = this._introWelcomeTextStylePortrait;
    this._introTapText.style = this._introTapTextStylePortrait;
  }

  private _initializeAssets(): void {
    const gameAtlasSpritesheet1 =
      Assets.getAsset<TGameAssetAlias>("atlasGame1");

    this._introSprite = new PIXI.Sprite(
      gameAtlasSpritesheet1.textures["bonus_intro_popup.webp"],
    );
    this._introSprite.anchor.set(0.5);
    // Fix the sprite horizontal offset.
    this._introSprite.position.set(-10, 0);
  }

  private _parentAssets(): void {
    this._view.addChild(this._introSprite);
  }

  private _initializeComponents(): void {
    this._introWelcomeText = new PIXI.Text({
      text: "CONGRATULATIONS\nYOU WON 10 FREE SPINS!",
      style: this._introWelcomeTextStyleLandscape,
    });
    this._introWelcomeText.anchor.set(0.5);

    this._introTapText = new PIXI.Text({
      text: "TAP ANYWHERE TO CONTINUE",
      style: this._introTapTextStyleLandscape,
    });
    this._introTapText.anchor.set(0.5);

    this._backgroundGraphics = new PIXI.Graphics();
    this._backgroundGraphics
      .rect(-2000, -2000, 4000, 4000)
      .fill({ color: 0x000000, alpha: 0.7 });
    this._backgroundGraphics.zIndex = -1;
  }

  private _parentComponents(): void {
    this._view.addChild(this._backgroundGraphics);
    this._view.addChild(this._introWelcomeText);
    this._view.addChild(this._introTapText);
  }
}
